<template>
  <div>
    <h5>Carrier Report</h5>

    <b-card class="mb-4 report-filters-card">
      <b-form inline class="report-filters" @submit.prevent="fetchReport">
        <ReportPeriodFilter
          :period-type.sync="periodType"
          :time-period.sync="timePeriod"
          :selected-dates.sync="selectedDates"
          :time-options="timeOptions"
          :date-config="dateConfig"
          radio-name="carrierReportPeriodType"
        />
        <button class="btn btn-primary">Show Report</button>
      </b-form>
      <small class="text-muted d-block mt-3">
        Dispatch metrics include archived orders. CD requests and carrier-fault undispatches use their event dates within the selected period.
      </small>
    </b-card>

    <LoadingIndicator v-if="loading" centered />
    <span v-else-if="empty">No records found</span>

    <b-card v-else-if="items.length" class="mb-4 report-results" no-body>
      <b-table responsive striped hover small :items="items" :fields="fields" class="mb-0" show-empty>
        <template #table-caption>Carrier activity from {{ periodLabel }}</template>
        <template #cell(carrierId)="data">
          <b-link :to="carrierLink(data.value)" target="_blank">{{ carrierName(data.item) }}</b-link>
        </template>
        <template #custom-foot="data">
          <b-tr class="font-weight-bold">
            <b-th v-for="field in data.fields" :key="field.key">
              {{ field.key === 'carrierId' ? 'Totals' : formatTotal(field.key) }}
            </b-th>
          </b-tr>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import moment from 'moment';
import ReportPeriodFilter from '@/components/pages/reports/ReportPeriodFilter';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import { ApiClient } from '@/api/ApiClient';
import { tableFields } from '@/mixins/methods';

export default {
  name: 'ReportCarriers',
  components: { LoadingIndicator, ReportPeriodFilter },
  mixins: [tableFields],
  data() {
    const currency = value => (value === null || value === undefined ? '-' : `$${Number(value).toLocaleString()}`);
    const percentage = value => (value === null || value === undefined ? '-' : `${value}%`);
    return {
      loading: false,
      empty: false,
      items: [],
      totals: {},
      periodType: 'list',
      timePeriod: 1,
      selectedDates: null,
      form: { periodStart: null, periodEnd: null },
      dateConfig: { mode: 'range', dateFormat: 'Y-m-d', altInput: true, altFormat: 'm/d/Y', allowInput: true },
      timeOptions: [
        { text: 'Current month', value: 1 },
        { text: 'Last month', value: 2 },
        { text: 'Current quarter', value: 3 },
        { text: 'Last quarter', value: 4 },
        { text: 'Current year', value: 5 },
      ],
      fields: [
        { key: 'carrierId', label: 'Carrier' },
        { key: 'dispatchedCount', label: 'Dispatched Orders', sortable: true },
        { key: 'averageBrokerFee', label: 'Avg Broker Fee', sortable: true, formatter: currency },
        { key: 'cdRequestsCount', label: 'CD Requests', sortable: true },
        { key: 'cdAbovePostedRateCount', label: 'Above Posted Rate', sortable: true },
        { key: 'carrierMistakesCount', label: 'Carrier Mistakes', sortable: true },
        { key: 'completedCount', label: 'Completed', sortable: true },
        { key: 'carrierMistakeRatio', label: 'Mistakes vs Completed', sortable: true, formatter: percentage },
        { key: 'approvalsCount', label: 'Approvals', sortable: true },
      ],
    };
  },
  computed: {
    periodLabel() {
      if (!this.form.periodStart) return 'all time';
      return `${this.form.periodStart} through ${this.form.periodEnd}`;
    },
  },
  methods: {
    prepareDates() {
      let dates = [];
      if (this.periodType === 'custom') {
        if (!this.selectedDates) return false;
        dates = this.selectedDates.split(' to ');
        if (dates.length < 2) dates.push(dates[0]);
      } else if (this.timePeriod === 1) dates = [moment().startOf('month'), moment().endOf('month')];
      else if (this.timePeriod === 2) dates = [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')];
      else if (this.timePeriod === 3) dates = [moment().startOf('quarter'), moment().endOf('quarter')];
      else if (this.timePeriod === 4) dates = [moment().subtract(1, 'quarter').startOf('quarter'), moment().subtract(1, 'quarter').endOf('quarter')];
      else if (this.timePeriod === 5) dates = [moment().startOf('year'), moment().endOf('year')];

      this.form.periodStart = dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null;
      this.form.periodEnd = dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null;
      if (moment(this.form.periodEnd).isAfter(moment(this.form.periodStart).add(1, 'year'))) {
        this.$bvToast.toast('The report period cannot be longer than one year.', {
          title: 'Invalid period',
          variant: 'danger',
          solid: true,
        });
        return false;
      }
      return true;
    },
    async fetchReport() {
      if (!this.prepareDates()) return;
      this.loading = true;
      try {
        const response = await ApiClient.reports.carriers({ params: this.form });
        this.items = response && response.data && response.data.data ? response.data.data.rows : [];
        this.totals = response && response.data && response.data.data ? response.data.data.totals : {};
        this.empty = !this.items.length;
      } finally {
        this.loading = false;
      }
    },
    carrierLink(id) {
      return { name: 'CarrierDetails', params: { id } };
    },
    carrierName(item) {
      return item.carrier && item.carrier.contact ? item.carrier.contact.company : `Carrier #${item.carrierId}`;
    },
    formatTotal(key) {
      const value = this.totals[key];
      if (key === 'averageBrokerFee') return value === null || value === undefined ? '-' : `$${Number(value).toLocaleString()}`;
      if (key === 'carrierMistakeRatio') return value === null || value === undefined ? '-' : `${value}%`;
      return this.formatReportValue(key, value);
    },
  },
};
</script>

<style lang="scss" scoped>
.report-results ::v-deep table {
  white-space: nowrap;
}
.report-filters {
  justify-content: flex-start;
  align-items: center;
  gap: 0.75rem;
}
.report-filters .btn {
  height: 38px;
}
@media (max-width: 767.98px) {
  .report-filters .btn {
    width: 100%;
  }
}
</style>
